import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useAuth } from "./useAuth";
// Not permissionService: this provider is mounted on every page, and that
// module carries the whole role and permission administration with it.
import { getUserPermissionKeys, getUserRole } from "@/services/userPermissionKeys";
import { debugLog, debugWarn } from "@/utils/debug";
import { invalidateIdentityCaches } from "@/utils/identityCacheRegistry";
import { fetchStaffSession } from "@/services/sessionBootstrap";
import type { Role } from "@/types/database.types";
import {
  PermissionsContext,
  type PermissionsContextType,
  type PermissionsState,
} from "./permissions-context";

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 500,
  shouldRetry?: (error: unknown) => boolean,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries || (shouldRetry && !shouldRetry(error))) {
        throw error;
      }

      const delay = baseDelayMs * Math.pow(2, attempt) + Math.random() * 100;
      console.log(
        `[usePermissions] Retry ${attempt + 1}/${maxRetries} after ${delay}ms`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

function emitPermissionsLoaded(): void {
  (window as any).__MOE_PERMISSIONS_LOADED__ = true;
  window.dispatchEvent(new CustomEvent("moe:permissions-loaded"));
}

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user } = useAuth();
  const [state, setState] = useState<PermissionsState>({
    permissionKeys: [],
    role: null,
    isLoading: true,
    hasLoadedOnce: false,
    error: null,
    lastRefreshed: null,
  });

  const isMountedRef = useRef(true);
  /** Which user's permissions are currently loaded. */
  const loadedForUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      debugLog("usePermissions", "No user ID, clearing permissions");
      setState({
        permissionKeys: [],
        role: null,
        isLoading: false,
        // Signed out: there is nothing to wait for, so the app must not stay
        // blocked on a permissions load that will never happen.
        hasLoadedOnce: true,
        error: null,
        lastRefreshed: null,
      });
      loadedForUserIdRef.current = null;
      emitPermissionsLoaded();
      return;
    }

    debugLog("usePermissions", "Fetching permissions for user:", user.id);

    // A refresh for the SAME user keeps hasLoadedOnce true, so AppReadyProvider
    // does not tear the whole tree down underneath whatever triggered the
    // refresh. A DIFFERENT user must block again, or they would briefly render
    // with the previous user's permissions.
    const isSameUser = loadedForUserIdRef.current === user.id;

    // Clear the short-lived identity caches, but only when this is a genuine
    // re-resolution for a user we have already loaded — which is what
    // refreshPermissions() does after a role or duty change, and the whole
    // point of that call is to not get the previous answer back.
    //
    // NOT on the first load for a user. AuthProvider has just resolved this
    // very session a moment earlier to get their profile, and that answer is
    // exactly the one we want; clearing it here would throw away a fresh
    // result and immediately ask the same question again. The caches are keyed
    // by user id, so a different user cannot be served a stale entry in any
    // case, and sign-out clears everything.
    if (isSameUser) {
      invalidateIdentityCaches();
    }
    setState((prev) => ({
      ...prev,
      isLoading: true,
      hasLoadedOnce: isSameUser ? prev.hasLoadedOnce : false,
      error: null,
    }));

    try {
      const shouldRetry = (error: unknown): boolean => {
        if (error && typeof error === "object" && "message" in error) {
          const msg = String((error as any).message || "");
          return (
            msg.includes("no rows") ||
            msg.includes("PGRST116") ||
            msg.includes("Failed to fetch user permissions")
          );
        }
        return false;
      };

      // One call for the keys AND the role.
      //
      // AuthProvider has usually just asked the same question to get this
      // user's profile, so the memo in front of it means this is normally
      // served without a request at all. What it replaces was four: an
      // es_users read and an RPC for the keys, then another es_users read and
      // an es_roles read for the role.
      //
      // A null result means the database does not have the function — a client
      // deployed ahead of its migration, or a developer pointed at an older
      // database — and we take the long way round. Any failure does the same,
      // because a slower answer beats no answer on the screen that decides
      // what this user is allowed to see.
      let bootstrap = null;
      try {
        bootstrap = await fetchStaffSession(user.id);
      } catch (bootstrapError) {
        debugWarn(
          "usePermissions",
          "Session bootstrap failed, falling back to per-table reads:",
          bootstrapError,
        );
      }

      if (!isMountedRef.current) return;

      let permKeys: string[] | null;
      let roleData: Role | null;

      if (bootstrap) {
        permKeys = bootstrap.permissionKeys;
        roleData = bootstrap.role;
        debugLog("usePermissions", "Session bootstrap resolved:", permKeys);
      } else {
        const keysResult = await retryWithBackoff(
          async () => {
            const result = await getUserPermissionKeys(
              user.id,
            );
            if (result.error) {
              throw new Error(result.error);
            }
            return result;
          },
          3,
          500,
          shouldRetry,
        );

        if (!isMountedRef.current) return;

        permKeys = keysResult.data;
        debugLog("usePermissions", "Permission keys fetched:", permKeys);

        const { data: fallbackRole, error: roleError } = await retryWithBackoff(
          async () => getUserRole(user.id),
          3,
          500,
          shouldRetry,
        );

        if (!isMountedRef.current) return;

        if (roleError) {
          debugWarn("usePermissions", "Could not fetch user role:", roleError);
        }
        roleData = fallbackRole;
      }
      debugLog("usePermissions", "Role data fetched:", roleData);
      debugLog(
        "usePermissions",
        "Is Super Admin:",
        roleData?.hierarchy_level === 100 || roleData?.name === "Super Admin",
      );

      // No third request here.
      //
      // This used to follow up with getActivePermissions() — a read of the
      // whole es_permissions table — purely to hydrate a `permissions` array
      // of full Permission objects on the context. Nothing in the app ever
      // read that array. Every consumer goes through hasPermission(),
      // hasAnyPermission(), isAdmin() and friends, all of which work off
      // `permissionKeys`. So it was a full-table read on every page load whose
      // result was discarded, and it has been removed along with the field, so
      // that a future consumer gets a compile error rather than an empty list.
      //
      // The pages that genuinely need Permission objects (the roles screen)
      // fetch them where they are used.

      if (!isMountedRef.current) return;

      setState({
        permissionKeys: permKeys || [],
        role: roleData || null,
        isLoading: false,
        hasLoadedOnce: true,
        error: null,
        lastRefreshed: new Date(),
      });
      loadedForUserIdRef.current = user.id;

      emitPermissionsLoaded();
    } catch (err) {
      console.error("Error fetching permissions:", err);

      if (!isMountedRef.current) return;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        // Unblock even on failure. An app stuck on a loading screen forever is
        // worse than one that renders and shows access-denied states.
        hasLoadedOnce: true,
        error:
          err instanceof Error ? err.message : "Failed to load permissions",
      }));
      loadedForUserIdRef.current = user.id;

      emitPermissionsLoaded();
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      if (
        state.role?.hierarchy_level === 100 ||
        state.role?.name === "Super Admin"
      ) {
        return true;
      }
      return state.permissionKeys.includes(permissionKey);
    },
    [state.permissionKeys, state.role],
  );

  const hasAnyPermission = useCallback(
    (permissionKeys: string[]): boolean => {
      if (
        state.role?.hierarchy_level === 100 ||
        state.role?.name === "Super Admin"
      ) {
        return true;
      }
      return permissionKeys.some((key) => state.permissionKeys.includes(key));
    },
    [state.permissionKeys, state.role],
  );

  const hasAllPermissions = useCallback(
    (permissionKeys: string[]): boolean => {
      if (
        state.role?.hierarchy_level === 100 ||
        state.role?.name === "Super Admin"
      ) {
        return true;
      }
      return permissionKeys.every((key) => state.permissionKeys.includes(key));
    },
    [state.permissionKeys, state.role],
  );

  const isSuperAdmin = useCallback((): boolean => {
    return (
      state.role?.hierarchy_level === 100 || state.role?.name === "Super Admin"
    );
  }, [state.role]);

  const isAdmin = useCallback((): boolean => {
    const level = state.role?.hierarchy_level || 0;
    return level >= 90;
  }, [state.role]);

  const canManage = useCallback(
    (
      entity: "users" | "roles" | "permissions" | "services" | "applications",
    ): boolean => {
      if (isSuperAdmin()) return true;

      const managePermissions: Record<string, string[]> = {
        users: [
          "users.view",
          "users.create",
          "users.edit",
          "users.delete",
          "users.approve",
        ],
        roles: [
          "roles.view",
          "roles.create",
          "roles.edit",
          "roles.delete",
          "roles.assign_permissions",
        ],
        permissions: [
          "permissions.view",
          "permissions.create",
          "permissions.edit",
          "permissions.delete",
        ],
        services: [
          "services.view",
          "services.create",
          "services.edit",
          "services.delete",
        ],
        applications: [
          "applications.view_all",
          "applications.approve",
          "applications.deny",
          "applications.assign",
        ],
      };

      return hasAnyPermission(managePermissions[entity] || []);
    },
    [hasAnyPermission, isSuperAdmin],
  );

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  const value: PermissionsContextType = useMemo(
    () => ({
      ...state,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
      isAdmin,
      canManage,
      refreshPermissions,
    }),
    [
      state,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      isSuperAdmin,
      isAdmin,
      canManage,
      refreshPermissions,
    ],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
